import React, { useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { Page, LayoutSingleColumn, NamedLink } from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './FAQPage.module.css';

const FAQ_CATEGORIES = [
  {
    id: 'getting-started',
    number: '01',
    title: 'Getting Started',
    subtitle: 'The basics, before you dive in.',
    items: [
      {
        question: 'What is Nothing To Wear?',
        answer:
          'A peer-to-peer marketplace where you rent, lend, buy, and sell fashion items directly with real people — not a warehouse, not a subscription box. We’re Kelowna, BC born and based, shipping and lending across Canada.',
      },
      {
        question: 'Do I need an account to browse?',
        answer:
          'You can look around without one, but you’ll need to create an account before you can rent, buy, lend, or sell anything. If you’re listing an item to lend or sell, you’ll also verify your identity through Stripe before you can get paid.',
      },
      {
        question: 'Is this available outside BC?',
        answer:
          'We ship across Canada. We’re just proudly based in Kelowna, and that’s where our roots (and our first closets) are.',
      },
      {
        question: 'How do I delete my account?',
        answer:
          'Log in, go to Account → Manage Account, check "I want to delete my account," enter your password, and confirm. This permanently removes your personal data, profile, and listings from the marketplace — it can\'t be undone. Some transaction records may be retained afterward as required under Canadian tax law; see our Privacy Policy for details.',
      },
    ],
  },
  {
    id: 'renting-buying',
    number: '02',
    title: 'Renting & Buying',
    subtitle: 'For when you’re browsing someone else’s closet.',
    items: [
      {
        question: 'What’s the difference between renting and buying?',
        answer:
          'Renting means the item is yours for a set period — a weekend, a week, whatever the Lender offers — then it goes back. Buying means it’s yours for good.',
      },
      {
        question: 'How do I know if something will actually fit?',
        answer:
          'Every listing includes sizing details and real photos from the Lender or Seller. Still not sure? Message them directly through the app before you book — that’s what it’s there for.',
      },
      {
        question: 'What’s the Buyer Protection Fee?',
        answer:
          'A mandatory 5% fee added to every rental. It covers minor accidental damage beyond ordinary wear — a small mark, a loose thread — so little mishaps don’t turn into a bill. It doesn’t cover larger accidental damage, which is still your responsibility (see below).',
      },
      {
        question: 'The item arrived damaged, or not as described. Now what?',
        answer:
          'Email us the same day you receive it, with photos. We’ll help you and the Lender or Seller sort it out.',
      },
      {
        question: 'Can I cancel a booking?',
        answer:
          'Yes — email us and we’ll take care of it directly. Cancellations aren’t automatic through checkout, so reach out rather than waiting on it to sort itself out.',
      },
      {
        question: 'What happens if I return a rental late?',
        answer:
          'A late fee applies automatically — 2× the item’s daily rental rate for every calendar day it’s outstanding past your Rental Period, with no grace period, capped at 200% of the item’s stated replacement value. Rentals run midnight to midnight, so the day it’s due matters, not the hour.',
      },
      {
        question: 'What if I damage something beyond what the Buyer Protection Fee covers?',
        answer: (
          <>
            You’re responsible for the cost, up to the item’s stated replacement value. Full
            detail is in our{' '}
            <NamedLink name="RenterBuyerPolicyPage">Renter &amp; Buyer Policy</NamedLink>.
          </>
        ),
      },
    ],
  },
  {
    id: 'lending-selling',
    number: '03',
    title: 'Lending & Selling',
    subtitle: 'For when your closet’s ready to work for you.',
    items: [
      {
        question: 'How much does it cost to list something?',
        answer:
          'Nothing to list. We take a 20% commission only when something actually rents or sells — you keep 80%, automatically.',
      },
      {
        question: 'How do I set up Stripe to get paid?',
        answer:
          'Before your first listing can go live, you\'ll be asked to connect a Stripe account — this is what actually pays you. It takes you to a short form hosted by Stripe (not us): choose whether you\'re an individual or a business, add your legal name and address, verify your identity, and enter your bank details. It\'s usually approved instantly, though Stripe sometimes takes 1–3 business days if they need to manually check a document. This is the same step covered in "Why do I need to verify my identity?" under Payments & Trust — it only needs to be done once.',
      },
      {
        question: 'When do I actually get paid?',
        answer:
          'Once a rental wraps up, the transaction completes automatically 2 days after the rental period ends (or sooner, at our discretion). From there, Stripe sends your payout to your bank account, which typically takes 5–10 business days to arrive.',
      },
      {
        question: 'What if a Renter returns my item late?',
        answer:
          'You’re compensated — 150% of the item’s daily rental rate for every calendar day it’s late, on top of your original payout.',
      },
      {
        question: 'What if my item comes back damaged?',
        answer:
          'Report it with photos the same day it’s returned, and we’ll help resolve it. Minor accidental damage is covered by the Renter’s Buyer Protection Fee; anything beyond that is the Renter’s responsibility, up to your item’s stated replacement value.',
      },
      {
        question: 'Can I say no to a rental or sale request?',
        answer: 'Of course — it’s your closet. You approve every request before it’s confirmed.',
      },
      {
        question: 'Do I need to give notice before cancelling a booking I’ve accepted?',
        answer:
          'Yes — 14 days’ notice for a future booking, given in good faith. Cancel with less notice, and the Renter gets a full refund while you don’t receive a payout for that booking.',
      },
    ],
  },
  {
    id: 'payments-trust',
    number: '04',
    title: 'Payments & Trust',
    subtitle: 'The stuff that has to be boring to be trustworthy.',
    items: [
      {
        question: 'How does payment actually work?',
        answer:
          'Everything runs through Stripe, a PCI DSS Level 1 certified payment processor — the highest security certification in the payments industry. We never see or store your full card details.',
      },
      {
        question: 'Why do I need to verify my identity?',
        answer:
          'If you’re listing an item to lend or sell, you’ll verify your identity through Stripe before you can get paid. It’s a big part of what makes trusting a stranger with your money — and your closet — actually work.',
      },
      {
        question: 'Something’s gone wrong. How do I reach a real person?',
        answer: (
          <>
            Email <a href="mailto:help@nothingtowearclub.ca">help@nothingtowearclub.ca</a>. A
            real person answers — not a bot, not a maze of menus.
          </>
        ),
      },
    ],
  },
  {
    id: 'policies',
    number: '05',
    title: 'Policies',
    subtitle: 'For the full, unabridged version.',
    items: [
      {
        question: 'Where can I read the full details behind these answers?',
        answer: (
          <>
            Our <NamedLink name="RenterBuyerPolicyPage">Renter &amp; Buyer Policy</NamedLink> and{' '}
            <NamedLink name="LenderSellerPolicyPage">Lender &amp; Seller Policy</NamedLink> cover
            everything above in full, alongside our{' '}
            <NamedLink name="TermsOfServicePage">Terms and Conditions</NamedLink> and{' '}
            <NamedLink name="PrivacyPolicyPage">Privacy Policy</NamedLink>.
          </>
        ),
      },
    ],
  },
];

const QAItem = props => {
  const { id, question, answer, isOpen, onToggle } = props;
  return (
    <div className={classNames(css.qaItem, { [css.open]: isOpen })}>
      <button
        className={css.qaQuestion}
        aria-expanded={isOpen}
        aria-controls={`${id}-answer`}
        onClick={() => onToggle(id)}
      >
        {question}
        <span className={css.plus} aria-hidden="true" />
      </button>
      <div className={css.qaAnswer} id={`${id}-answer`}>
        <div className={css.qaAnswerInnerWrap}>
          <div className={css.qaAnswerInner}>{answer}</div>
        </div>
      </div>
    </div>
  );
};

export const FAQPageComponent = props => {
  const { scrollingDisabled } = props;
  const [openItems, setOpenItems] = useState(new Set());

  const toggleItem = id => {
    setOpenItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <Page title="FAQ" scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.page}>
          <section className={css.hero}>
            <span className={css.eyebrow}>FAQ</span>
            <h1 className={css.heroTitle}>
              Questions? <i className={css.heroTitleItalic}>Fair enough.</i>
            </h1>
            <p className={css.subhead}>
              Everything you’re wondering before you rent, buy, lend, or sell — answered
              plainly, no fine print required to find it.
            </p>

            <nav className={css.catnav}>
              {FAQ_CATEGORIES.map(category => (
                <a key={category.id} href={`#${category.id}`} className={css.catnavLink}>
                  {category.title}
                </a>
              ))}
            </nav>
          </section>

          <section className={css.faq}>
            {FAQ_CATEGORIES.map(category => (
              <div key={category.id} className={css.category} id={category.id}>
                <div className={css.categoryHead}>
                  <span className={css.categoryNum}>{category.number}</span>
                  <h2 className={css.categoryTitle}>{category.title}</h2>
                </div>
                <p className={css.categorySub}>{category.subtitle}</p>

                {category.items.map((item, index) => {
                  const itemId = `${category.id}-${index}`;
                  return (
                    <QAItem
                      key={itemId}
                      id={itemId}
                      question={item.question}
                      answer={item.answer}
                      isOpen={openItems.has(itemId)}
                      onToggle={toggleItem}
                    />
                  );
                })}
              </div>
            ))}
          </section>

          <section className={css.cta}>
            <h2 className={css.ctaHeading}>
              Still stuck? <i className={css.ctaHeadingItalic}>We’re around.</i>
            </h2>
            <p className={css.ctaText}>No bots, no runaround — just email us and we’ll actually answer.</p>
            <div className={css.ctaButtons}>
              <a className={classNames(css.btn, css.btnSolid)} href="mailto:help@nothingtowearclub.ca">
                Email us
              </a>
              <NamedLink name="HowItWorksPage" className={classNames(css.btn, css.btnGhost)}>
                See how it works
              </NamedLink>
            </div>
          </section>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => {
  return {
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const FAQPage = compose(connect(mapStateToProps))(FAQPageComponent);

export default FAQPage;
